import React, { useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { useCombobox, UseComboboxState, UseComboboxStateChangeOptions } from 'downshift';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { MESSAGES } from '../../../../../CommonConstants';
import { DEBOUNCE_TIME } from './SearchActions.meta';
import { EntityDTO, ProviderType } from '../../../../DocumentVault.meta';
import { EntitySearchResults } from './components/EntitySearchResults/EntitySearchResults';
import { useSearchEntityList } from './useSearchEntityList';
import { VideoModal } from '../../../VideoModal/VideoModal';
import { FullPageLoading } from '../../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { useRouteParams } from '../../../../hooks/useRouteParams';
import { useListEntitiesQuery } from '../../../../api/useListEntitiesQuery';
import { SearchActionsLoading } from './SearchActions.loading';

const stateReducer = (
  state: UseComboboxState<EntityDTO>,
  actionAndChanges: UseComboboxStateChangeOptions<EntityDTO>,
) => {
  const { type, changes } = actionAndChanges;

  switch (type) {
    case useCombobox.stateChangeTypes.InputBlur:
    case useCombobox.stateChangeTypes.InputKeyDownEscape:
    case useCombobox.stateChangeTypes.ItemClick:
    case useCombobox.stateChangeTypes.InputKeyDownEnter:
      return {
        ...changes,
        inputValue: '',
      };
    default:
      return changes;
  }
};

export const SearchActions = () => {
  const { clientId, path, providerType, filterableByYear } = useRouteParams();
  const { isLoading, isError, data } = useListEntitiesQuery({
    clientId,
    path,
    providerType,
    filterableByYear,
  });

  const isExternalContentAvailable = useMemo(() => {
    if (path === '') {
      return data ? data.items.some(item => item.providerType === ProviderType.External) : false;
    }
    return false;
  }, [path, data]);

  if (isLoading) {
    return <SearchActionsLoading />;
  }
  if (isError) {
    return null;
  }
  if (isExternalContentAvailable) {
    return null;
  }

  return <SearchActionsContent disabled={!data || data.items.length === 0} />;
};

interface SearchActionsContentProps {
  disabled: boolean;
}

const SearchActionsContent = ({ disabled }: SearchActionsContentProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const {
    isFetching,
    isDownloadingFile,
    currentVideoEntity,
    isError,
    data,
    actions: { onEntityPress, setCurrentVideoEntity },
  } = useSearchEntityList({ searchTerm });

  const debouncedSetSearchTerm = useRef(
    debounce((term: string) => setSearchTerm(term), DEBOUNCE_TIME),
  ).current;

  useEffectOnce(() => () => {
    debouncedSetSearchTerm.cancel();
  });

  const comboboxBag = useCombobox({
    items: data?.items ?? [],
    itemToString: item => (item ? `${item.name}` : ''),
    onInputValueChange: ({ inputValue }) => {
      debouncedSetSearchTerm(inputValue || '');
    },
    onSelectedItemChange({ selectedItem }) {
      if (!!selectedItem) {
        setSearchTerm('');
        onEntityPress(selectedItem);
      }
    },
    stateReducer,
  });

  const { getComboboxProps, getInputProps } = comboboxBag;

  return (
    <>
      {isDownloadingFile && <FullPageLoading />}
      <Box position="relative" width={320}>
        <Box {...getComboboxProps()}>
          <Input
            {...getInputProps({
              'aria-label': 'Search Documents',
              'aria-labelledby': undefined,
            })}
            leftIcon="far fa-search"
            placeholder="Search Current Folder"
            formVariant={isError ? 'error' : undefined}
            helperText={isError ? MESSAGES.GENERIC_ERROR : undefined}
            disabled={disabled}
          />
        </Box>
        <EntitySearchResults
          isLoading={isFetching}
          entityList={data?.items}
          comboboxBag={comboboxBag}
          searchTerm={searchTerm}
        />
      </Box>
      <VideoModal entity={currentVideoEntity} onClose={() => setCurrentVideoEntity(null)} />
    </>
  );
};
